import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Link, useParams } from "react-router-dom"
import { ArrowLeft, User, CalendarClock, Check, Phone, Search, ListChecks } from "lucide-react"
import { api } from "@/lib/api"
import { Loading, ErrorBox, fmtRelative } from "@/components/portal/LoadingError"
import { StatusBadge } from "@/components/portal/StatusBadge"
import { StatusActions } from "@/components/portal/StatusActions"
import { CreateContactModal } from "@/components/portal/CreateContactModal"
import { useContactDraftRestore } from "@/hooks/useContactDraftRestore"
import { CallTaskModal } from "@/components/portal/CallTaskModal"
import { HeroInfoPopover } from "@/components/portal/HeroInfoPopover"
import { HeroVorgangName } from "@/components/portal/HeroVorgangName"
import { cn } from "@/lib/utils"

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: "include", ...init })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(body.error || body.detail || `HTTP ${res.status}`)
  return body as T
}

/** Meiti-Anruf-Detail (KI-Telefon). Nutzt /admin/calls/{id}/*-Endpoints direkt. */
export default function MeitiCallDetail() {
  const { id } = useParams<{ id: string }>()
  const callId = Number(id)
  const qc = useQueryClient()

  // Page-Level Draft-Restore: triggert auch wenn der "Neuen Kontakt"-Button im UI nicht sichtbar ist
  // (z.B. weil bereits ein HERO-Kontakt verknuepft ist). Restore oeffnet Modal trotzdem.
  const pageRestore = useContactDraftRestore(callId)

  const { data, isLoading, error } = useQuery({
    queryKey: ["meiti-call", id],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/calls", {
        params: { query: { limit: 500 } },
      })
      if (error) throw new Error(String(error))
      return data.items.find((c) => c.id === callId)
    },
    enabled: !!id,
  })

  if (isLoading) return <Loading label="Lädt Anruf..." />
  if (error) return <div className="p-10"><ErrorBox error={error} /></div>
  if (!data) return <div className="p-10 text-muted-foreground">Anruf #{callId} nicht gefunden.</div>

  // HERO-Bezeichnung des Vorgangs aus dem lokalen Projekt-Cache — von Hand
  // ergaenzt, weil `api-types.ts` generiert ist.
  const c = data as typeof data & { hero_project_nr?: string | null; hero_project_title?: string | null }
  const name = `${c.first_name} ${c.last_name}`.trim() || "(unbekannt)"
  const onInvalidate = () => qc.invalidateQueries({ queryKey: ["meiti-call", id] })

  return (
    <div className="px-10 py-8 max-w-[1100px]">
      {/* Page-Level Restore-Modal — rendert immer wenn Draft zum Call passt */}
      {pageRestore.restoreOpen && pageRestore.draft && (
        <CreateContactModal
          open={true}
          onClose={pageRestore.consume}
          mode="meiti"
          itemId={callId}
          tenantSlug={c.tenant_slug || undefined}
          onSuccess={onInvalidate}
          draftId={pageRestore.draft.id}
          draftPrefill={pageRestore.draft}
          initialWithProject={pageRestore.draft.withProject}
          projectOnly={pageRestore.draft.projectOnly}
        />
      )}
      <Link to="/calls" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Zurück zur KI-Telefon-Liste
      </Link>

      <div className="rounded-lg border bg-card shadow-xs overflow-hidden">
        <div className="px-6 py-4 border-b bg-secondary flex items-start justify-between gap-4">
          <div>
            <h1 className="text-[18px] font-bold">{name}</h1>
            <div className="text-[13px] text-muted-foreground mt-1.5 space-y-0.5">
              <div><Phone className="w-3 h-3 inline" /> {c.phone}</div>
              <div>Tenant: {c.tenant_slug} · {fmtRelative(c.called_at)}</div>
              {c.hero_contact_id && (
                <div>
                  <HeroInfoPopover tenantSlug={c.tenant_slug || ""} contactId={c.hero_contact_id} projectId={c.hero_project_id}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--accent-color)", cursor: "help" }}>
                      <User className="w-3 h-3" /> HERO-Kontakt #{c.hero_contact_id}
                    </span>
                  </HeroInfoPopover>
                </div>
              )}
              {c.hero_project_id && (
                <div>
                  <HeroInfoPopover tenantSlug={c.tenant_slug || ""} contactId={c.hero_contact_id} projectId={c.hero_project_id}>
                    <span style={{ color: "var(--accent-color)", cursor: "help" }}>
                      HERO-Vorgang: <HeroVorgangName tenantSlug={c.tenant_slug} projectId={c.hero_project_id} nr={c.hero_project_nr} titel={c.hero_project_title} />
                    </span>
                  </HeroInfoPopover>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <StatusBadge status={c.status} />
            <StatusActions
              current={c.status}
              options={["open", "done", "booked", "no_booking"]}
              labels={{ open: "Offen", done: "Erledigt", booked: "Gebucht", no_booking: "Keine Buchung" }}
              mutate={async (s) => {
                const { error } = await api.PATCH("/api/v1/calls/{call_id}", {
                  params: { path: { call_id: c.id } },
                  body: { status: s },
                })
                if (error) throw new Error(String(error))
              }}
              invalidateKeys={[["meiti-calls"], ["meiti-call", id]]}
              size="sm"
            />
          </div>
        </div>

        {c.anfrage && (
          <div className="px-6 py-4 border-b">
            <div className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Anfrage</div>
            <div className="text-[14px]">{c.anfrage}</div>
          </div>
        )}

        {c.zusammenfassung && (
          <div className="px-6 py-4 border-b">
            <div className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Zusammenfassung</div>
            <div className="text-[13px] whitespace-pre-wrap leading-relaxed">{c.zusammenfassung}</div>
          </div>
        )}

        {/* HERO Auto-Search */}
        <HeroAutoSearch
          callId={c.id}
          heroContactId={c.hero_contact_id}
          tenantSlug={c.tenant_slug || undefined}
          onChange={() => qc.invalidateQueries({ queryKey: ["meiti-call", id] })}
        />

        {/* Booking */}
        <MeitiBooking
          callId={c.id}
          heroContactId={c.hero_contact_id}
          heroBookingId={c.hero_booking_id}
          defaultTitle={name}
          onBooked={() => qc.invalidateQueries({ queryKey: ["meiti-call", id] })}
        />

        {/* Aufgabe erstellen */}
        <MeitiTaskSection callId={c.id} tenantSlug={c.tenant_slug} defaultTitle={`Rückruf: ${name}`} />
      </div>

    </div>
  )
}

function MeitiTaskSection({
  callId, tenantSlug, defaultTitle,
}: {
  callId: number
  tenantSlug: string
  defaultTitle: string
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className="px-6 py-4 border-t">
      <div className="flex items-center justify-between">
        <div className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">Aufgabe</div>
        <button
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md bg-primary/10 text-primary hover:bg-primary/20"
        >
          <ListChecks className="w-3.5 h-3.5" /> Aufgabe erstellen
        </button>
      </div>
      <CallTaskModal
        open={open}
        onClose={() => setOpen(false)}
        mode="meiti"
        callId={callId}
        tenantSlug={tenantSlug}
        defaultTitle={defaultTitle}
      />
    </div>
  )
}

function HeroAutoSearch({
  callId, heroContactId, tenantSlug, onChange,
}: {
  callId: number
  heroContactId?: string | null
  tenantSlug?: string
  onChange: () => void
}) {
  const [result, setResult] = useState<any>(null)

  const search = useMutation({
    mutationFn: () => fetchJson<any>(`/api/v1/calls/${callId}/hero-search`),
    onSuccess: setResult,
  })

  const link = useMutation({
    mutationFn: () => fetchJson(`/api/v1/calls/${callId}/hero-contact`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ contact_id: result.contact_id }),
    }),
    onSuccess: () => { onChange(); setResult(null) },
  })

  return (
    <div className="px-6 py-4 border-b">
      <div className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        HERO-Kontakt
      </div>
      {heroContactId ? (
        <div className="text-[13px] text-muted-foreground">
          Bereits verknüpft mit Kontakt #{heroContactId}.
        </div>
      ) : (
        <>
          {/* Neu anlegen-Button gut sichtbar oben */}
          <div style={{ marginBottom: 10 }}>
            <NeuKontaktButton callId={callId} tenantSlug={tenantSlug} onChange={onChange} />
          </div>
          {/* Bestehenden suchen darunter */}
        <div>
          {!result && (
            <button
              onClick={() => search.mutate()}
              disabled={search.isPending}
              className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-3 py-1.5 rounded-md bg-primary/10 text-primary hover:bg-primary/20"
            >
              <Search className="w-3.5 h-3.5" /> {search.isPending ? "Sucht in HERO..." : "In HERO suchen"}
            </button>
          )}
          {result?.found === false && (
            <div>
              <div className="text-[12.5px] text-muted-foreground mb-2">
                Kein HERO-Kontakt gefunden.
              </div>
              <NeuKontaktButton callId={callId} tenantSlug={tenantSlug} onChange={onChange} />
            </div>
          )}
          {result?.found === true && (
            <div className="p-3 rounded-md bg-primary/5 border border-primary/20">
              <div className="text-[13.5px] font-medium">
                {result.first_name} {result.last_name}
              </div>
              <div className="text-[11.5px] text-muted-foreground">
                {[result.email, result.phone, `${result.zip} ${result.city}`].filter(Boolean).join(" · ")}
              </div>
              <button
                onClick={() => link.mutate()}
                disabled={link.isPending}
                className="mt-2 inline-flex items-center gap-1 text-[12px] font-medium px-3 py-1 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Check className="w-3.5 h-3.5" /> Verknüpfen
              </button>
            </div>
          )}
        </div>
        </>
      )}
    </div>
  )
}

function NeuKontaktButton({ callId, tenantSlug, onChange }: { callId: number; tenantSlug?: string; onChange: () => void }) {
  const [open, setOpen] = useState(false)
  const [withProject, setWithProject] = useState(true)
  const { restoreOpen, draft, consume } = useContactDraftRestore(callId)
  const openWith = (wp: boolean) => { setWithProject(wp); setOpen(true) }
  return (
    <>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <button
          onClick={() => openWith(true)}
          className="pp-btn pp-btn-sm"
          style={{ background: "#10b981", color: "#fff", borderColor: "#10b981" }}
        >
          + Kontakt + Projekt
        </button>
        <button
          onClick={() => openWith(false)}
          className="pp-btn pp-btn-sm"
        >
          + Nur Kontakt
        </button>
      </div>
      <CreateContactModal
        open={open || restoreOpen}
        onClose={() => { setOpen(false); consume() }}
        mode="meiti"
        itemId={callId}
        tenantSlug={tenantSlug}
        onSuccess={onChange}
        draftId={draft?.id}
        draftPrefill={draft || undefined}
        initialWithProject={draft?.withProject ?? withProject}
        projectOnly={draft?.projectOnly}
      />
    </>
  )
}

function MeitiBooking({
  callId, heroContactId, heroBookingId, defaultTitle, onBooked,
}: {
  callId: number
  heroContactId?: string | null
  heroBookingId?: string | null
  defaultTitle: string
  onBooked: () => void
}) {
  const [open, setOpen] = useState(false)
  const [slot, setSlot] = useState("")
  const [duration, setDuration] = useState("2")
  const [title, setTitle] = useState(defaultTitle)
  const [categoryId, setCategoryId] = useState("")
  const [description, setDescription] = useState("")
  const [err, setErr] = useState<string | null>(null)

  const opts = useQuery<any>({
    queryKey: ["meiti-calendar-options", callId],
    queryFn: () => fetchJson(`/api/v1/calls/${callId}/calendar-options`),
    enabled: open && !!heroContactId,
  })

  const book = useMutation({
    mutationFn: () => fetchJson(`/api/v1/calls/${callId}/book`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        start_datetime: slot ? new Date(slot).toISOString() : null,
        // "tag3" -> 3 ganze Tage; sonst Stunden (#13).
        ...(duration.startsWith("tag")
          ? { ganztags_tage: parseInt(duration.slice(3), 10) }
          : { duration_hours: parseFloat(duration) }),
        title,
        description,
        category_id: categoryId ? parseInt(categoryId) : null,
      }),
    }),
    onSuccess: () => { setOpen(false); onBooked() },
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  })

  return (
    <div className="px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">Termin</div>
        {heroBookingId ? (
          <span className="inline-flex items-center gap-1 text-[12px] text-success font-semibold">
            <Check className="w-3 h-3" /> Gebucht #{heroBookingId}
          </span>
        ) : (
          <button
            onClick={() => setOpen(!open)}
            disabled={!heroContactId}
            className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50"
          >
            <CalendarClock className="w-3.5 h-3.5" /> Termin anlegen
          </button>
        )}
      </div>
      {open && !heroBookingId && (
        <div className="mt-3 p-3 rounded-md border bg-secondary/30 space-y-3">
          {err && <div className="p-2 rounded-md bg-destructive/10 text-destructive text-[12px]">{err}</div>}
          <div className="grid grid-cols-2 gap-3">
            <input type="datetime-local" value={slot} onChange={(e) => setSlot(e.target.value)} className="rounded-md border bg-background px-2 py-1.5 text-[13px]" />
            <select value={duration} onChange={(e) => setDuration(e.target.value)} className="rounded-md border bg-background px-2 py-1.5 text-[13px]">
              <option value="1">1 Stunde</option>
              <option value="1.5">1,5 Stunden</option>
              <option value="2">2 Stunden</option>
              <option value="3">3 Stunden</option>
              <option value="4">4 Stunden</option>
              <option value="6">6 Stunden</option>
              <option value="8">8 Stunden</option>
              {/* Ganztaegig ueber mehrere Tage (#13). Dieselbe Liste wie im
              BookingModal — sie fehlte hier, deshalb liess sich aus der
              Anrufansicht kein mehrtaegiger Termin anlegen. */}
              <option value="tag2">2 Tage (ganztägig)</option>
              <option value="tag3">3 Tage (ganztägig)</option>
              <option value="tag4">4 Tage (ganztägig)</option>
              <option value="tag5">5 Tage (ganztägig)</option>
              <option value="tag10">10 Tage (ganztägig)</option>
            </select>
          </div>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titel" className="w-full rounded-md border bg-background px-2 py-1.5 text-[13px]" />
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="w-full rounded-md border bg-background px-2 py-1.5 text-[13px]">
            <option value="">– Kategorie –</option>
            {opts.data?.categories?.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Beschreibung (optional)"
            className="w-full rounded-md border bg-background px-2 py-1.5 text-[13px]"
          />
          {/* #148: dieser Dialog hat keine Mitarbeiter-Wahl — der Server nimmt
              den ersten freien Monteur. Eine Rueckfrage bei jeder Buchung
              waere Laerm, den man nicht beheben kann; der Hinweis steht
              deshalb fest im Dialog. */}
          <div className="text-[11.5px] text-muted-foreground" role="note">
            Mitarbeiter wird automatisch gewählt (erster freier Monteur). Ist keiner frei
            oder hinterlegt, steht der Termin in HERO ohne Mitarbeiter — dann im Kalender nachtragen.
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setOpen(false)} className="text-[12.5px] px-3 py-1.5 rounded-md bg-secondary hover:bg-secondary/70">Abbrechen</button>
            <button
              disabled={!slot || book.isPending}
              onClick={() => { setErr(null); book.mutate() }}
              className={cn(
                "text-[12.5px] font-medium px-4 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-1.5",
                (!slot || book.isPending) && "opacity-50 cursor-not-allowed",
              )}
            >
              <Check className="w-3.5 h-3.5" /> {book.isPending ? "Bucht..." : "Buchen"}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
